import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TrendingUp, Building2, ChevronRight, Loader2 } from "lucide-react";
import { API } from "@/App";
import axios from "axios";

const AMC_LIST = [
  "HDFC", "ICICI Prudential", "SBI", "Axis", "Kotak", "Nippon India", 
  "Aditya Birla Sun Life", "UTI", "DSP", "Mirae Asset", "Tata", 
  "Bandhan", "IDFC", "Motilal Oswal", "Edelweiss", "Franklin India",
  "Sundaram", "PGIM India", "Invesco", "L&T", "Canara Robeco",
  "Mahindra Manulife", "Quant", "Parag Parikh", "HSBC", "Baroda BNP Paribas"
];

export const TopFundsAMCPage = () => {
  const [selectedAMC, setSelectedAMC] = useState("HDFC");
  const [funds, setFunds] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchFunds = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/funds/search?q=${selectedAMC}&limit=20`);
        // Filter for Direct Growth funds and sort by name
        const filtered = (res.data.funds || [])
          .filter(f => f.planType === "Direct" && f.option === "Growth")
          .slice(0, 15);
        setFunds(filtered);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchFunds();
  }, [selectedAMC]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-32 pb-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="font-serif text-3xl sm:text-4xl font-bold mb-4">
              Top Performing Funds - <span className="gold-text">AMC Wise</span>
            </h1>
            <p className="text-muted-foreground">
              Discover the best performing funds from each Asset Management Company
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <Card className="bg-card border-border mb-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-primary" />
                  Select AMC
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Select value={selectedAMC} onValueChange={setSelectedAMC}>
                  <SelectTrigger data-testid="amc-select">
                    <SelectValue placeholder="Select AMC" />
                  </SelectTrigger>
                  <SelectContent>
                    {AMC_LIST.map((amc) => (
                      <SelectItem key={amc} value={amc}>{amc} Mutual Fund</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>

            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : (
              <div className="space-y-3">
                <h2 className="text-xl font-semibold flex items-center gap-2 mb-4">
                  <TrendingUp className="w-5 h-5 text-green-500" />
                  {selectedAMC} Funds (Direct - Growth)
                </h2>
                
                {funds.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No funds found for this AMC</p>
                ) : (
                  funds.map((fund, idx) => (
                    <Link
                      key={fund.schemeCode}
                      to={`/research/${fund.schemeCode}`}
                      className="block"
                    >
                      <Card className="bg-card border-border hover:border-primary/50 transition-all">
                        <CardContent className="py-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
                                {idx + 1}
                              </div>
                              <div>
                                <p className="font-medium text-sm line-clamp-1">{fund.schemeName}</p>
                                <p className="text-xs text-muted-foreground">{fund.category}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <span className={`text-xs px-2 py-1 rounded ${
                                fund.risk === "Very High" ? "bg-red-500/10 text-red-500" :
                                fund.risk === "High" ? "bg-orange-500/10 text-orange-500" :
                                fund.risk === "Moderate" ? "bg-yellow-500/10 text-yellow-500" :
                                "bg-green-500/10 text-green-500"
                              }`}>
                                {fund.risk}
                              </span>
                              <ChevronRight className="w-4 h-4 text-muted-foreground" />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};
